// coffee-branch.js — Morning Mode branch resolution.
//
// The app picks the branch; the user doesn't choose from a menu. Only bean state
// + yesterday's verdict decide it. Precedence (spec): Blend → New Bag → Repeat/Tweak.
//
//   Blend    remnant below one brew's dose AND a usable new bag on hand
//            (finishing the remnant is the point → wins even if the new bag is
//            also registerable).
//   New Bag  a freshly registered bag with no usable remnant (never brewed yet).
//   Repeat   enough beans + liked yesterday (👍).
//   Tweak    enough beans + disliked yesterday (👎).
//   (unrated yesterday ⇒ needsRating: ask 👍/👎 first, THEN Repeat vs Tweak —
//    never wire unrated straight to the tweak chips.)
//
// Pure + dependency-free so it unit-tests as a matrix (bean state × rating).

// The rating verdict on a brew: "up" | "down" | null. Prefers an explicit
// coffee_check_ins.would_repeat; falls back to brew_logs.rating (1-5, ≥4 = up).
export function ratingForBrew(brew, checkIns) {
  if (!brew) return null;
  const ci = (checkIns || []).find(c => c.brew_log_id === brew.id);
  if (ci && typeof ci.would_repeat === "boolean") return ci.would_repeat ? "up" : "down";
  if (ci && ci.rating != null) return ci.rating >= 4 ? "up" : "down";
  if (brew.rating != null) return brew.rating >= 4 ? "up" : "down";
  return null;
}

// Beans still in play (not finished, weight remaining).
function activeBags(beans) {
  return (beans || []).filter(b => b.status !== "finished" && +(b.weight_left_g ?? b.weight_g ?? 0) > 0);
}

// A bag never brewed from (no brew_log references it) and effectively full.
function isFreshBag(bean, brewLogs) {
  const brewed = (brewLogs || []).some(l => l.bean_id === bean.id);
  const left = +(bean.weight_left_g ?? bean.weight_g ?? 0);
  const total = +(bean.weight_g ?? left);
  const untouched = total > 0 ? left >= total - 2 : true; // within dust of full
  return !brewed && untouched;
}

/**
 * Resolve which branch Morning Mode should open on.
 * @param {object} p
 * @param {Array}  p.beans        bean rows (with status, weight_left_g, weight_g)
 * @param {Array}  p.brewLogs     brew_logs, newest first
 * @param {Array}  p.checkIns     coffee_check_ins
 * @param {object} p.preferences  open_bean_ids / last_bean_id
 * @param {number} p.doseG        dose (grams) for the default brew at `cups`
 * @param {number} [p.cups]       cups for the default brew (for the prompt copy)
 * @param {string} [p.forced]     user override: 'repeat'|'tweak'|'new_bag'|'blend'
 * @returns {{branch, reason, needsRating, activeBean, remnant, newBag, yesterdayBrew, rating}}
 */
export function resolveMorningBranch({ beans, brewLogs, checkIns, preferences, doseG, cups = 8, forced }) {
  const active = activeBags(beans);
  const byId = (id) => active.find(b => b.id === id);
  // The bag physically in the canister: last-brewed open bag, else first active.
  const openIds = preferences?.open_bean_ids;
  const openList = Array.isArray(openIds) ? active.filter(b => openIds.includes(b.id)) : active;
  const activeBean = byId(preferences?.last_bean_id) || openList[0] || active[0] || null;

  const yesterdayBrew = (brewLogs || [])[0] || null;
  const rating = ratingForBrew(yesterdayBrew, checkIns);

  const remnantLeft = activeBean ? +(activeBean.weight_left_g ?? activeBean.weight_g ?? 0) : 0;
  const oneBrewDose = +doseG || 0;
  const remnantLow = !!activeBean && oneBrewDose > 0 && remnantLeft < oneBrewDose;

  // A fresh bag that ISN'T the current remnant (candidate to blend into / open new).
  const newBag = active.find(b => b.id !== activeBean?.id && isFreshBag(b, brewLogs)) || null;
  const activeBeanFresh = activeBean && isFreshBag(activeBean, brewLogs);

  const mk = (branch, reason, extra = {}) => ({
    branch, reason, needsRating: false,
    activeBean, remnant: activeBean, newBag, yesterdayBrew, rating, cups, doseG: oneBrewDose,
    ...extra,
  });

  // Explicit user override always wins.
  if (forced) return mk(forced, "You picked this.");

  // Precedence 1 — Blend: remnant below a brew's dose AND a new bag on hand.
  if (remnantLow && newBag) {
    return mk("blend", `~${Math.round(remnantLeft)}g of ${activeBean.name} left — blend it with ${newBag.name}.`);
  }

  // Precedence 2 — New Bag: a freshly registered bag with no usable remnant.
  if (activeBeanFresh || (newBag && !activeBean)) {
    const bag = activeBeanFresh ? activeBean : newBag;
    return mk("new_bag", `${bag.name} is a fresh bag — let's dial in a starting recipe.`, { newBag: bag });
  }

  // No brews yet at all and no fresh bag detected → New Bag entry.
  if (!yesterdayBrew && activeBean) {
    return mk("new_bag", `First brew from ${activeBean.name}.`);
  }

  // Precedence 3 — Repeat/Tweak by rating. Unrated ⇒ ask first.
  if (rating == null && yesterdayBrew) {
    return { ...mk("repeat", "Rate yesterday's cup to continue."), needsRating: true };
  }
  if (rating === "down") return mk("tweak", "You didn't love yesterday's cup — let's adjust.");
  return mk("repeat", "Yesterday was 👍 — same recipe.");
}
